import type { ProductItem, ProductSearchParams } from "@/features/products/types";
import { apiFetch } from "@/lib/auth-api";

export interface ContractListItem {
	contractId: number;
	producerId?: number | null;
	name: string;
	producerName: string;
	year: number;
	quarter: string;
	contractType: string;
	pricingMode: string;
	ownerUserId?: number;
	ownerName?: string;
	ownerEmail?: string;
	isActive: boolean;
	producerSettled: boolean;
	producerSettledAt?: string | null;
	basketCount: number;
	startDate: string;
	endDate: string;
	exfactoryLists?: string;
}

export interface ContractOwnerCandidate {
	id: number;
	name: string;
	email: string;
}

export interface ProductBrief {
	bloz07: string;
	name: string;
	brandId?: number;
}

export interface ContractBrandDetail {
	brandId?: number;
	name: string;
	minPurchaseExfactoryValue: number;
	purchaseValue100Exfactory: number;
	settlementTiers: ContractBrandSettlementTierDetail[];
}

export interface ContractBrandSettlementTierDetail {
	tierId?: number;
	thresholdPercent: number;
	settlementPercent?: number;
	settlementAmount?: number;
}

export interface ContractProductSurchargeDetail {
	surchargeId?: number;
	bloz07: string;
	name: string;
	doplataSellInProcent?: number;
	doplataSellInKwota?: number;
	doplataSellOutProcent?: number;
	doplataSellOutKwota?: number;
}

export interface ContractMslItemDetail {
	itemId?: number;
	bloz07: string;
	name: string;
	mslDostepnosc: number;
	procentWymagalnosci?: number;
}

export interface ContractSurchargeSettlementRow {
	bloz07: string;
	productName: string;
	region: string;
	unitname: string;
	idfirm: number;
	doplataSellInProcent?: number;
	doplataSellInKwota?: number;
	doplataSellOutProcent?: number;
	doplataSellOutKwota?: number;
	purchaseQuantity: number;
	sellInPctValue: number;
	sellInKwotaValue: number;
	salesQuantity: number;
	sellOutPctValue: number;
	sellOutKwotaValue: number;
	totalSurchargeValue: number;
}

export interface SettlementTierDetail {
	tierId?: number;
	salesFrom: number;
	salesTo?: number;
	purchaseFrom: number;
	purchaseTo?: number;
	salesPercent: number;
	purchasePercent: number;
}

export interface SettlementDetail {
	settlementId?: number;
	startDate: string;
	endDate: string;
	totalSalesValue?: number;
	totalPurchaseValue?: number;
	marketingFixedAmount?: number;
	marketingFixedPercent?: number;
	marketingPurchaseAmount?: number;
	marketingSalesAmount?: number;
	marketingSalesPercent?: number;
	marketingPurchasePercent?: number;
	exhibitionFixedAmount?: number;
	exhibitionFixedPercent?: number;
	exhibitionPurchaseAmount?: number;
	exhibitionPurchasePercent?: number;
	exhibitionSalesPercent?: number;
	mslFixedAmount?: number;
	mslFixedPercent?: number;
	mslPurchaseAmount?: number;
	mslPurchasePercent?: number;
	mslSalesAmount?: number;
	mslSalesPercent?: number;
	reportingPurchaseAmount?: number;
	reportingPurchasePercent?: number;
	reportingSalesPercent?: number;
	reportingSalesAmount?: number;
	tiers: SettlementTierDetail[];
}

export interface BasketDetail {
	basketId?: number;
	name: string;
	products: ProductBrief[];
	settlements: SettlementDetail[];
}

export interface ExFactoryBrief {
	id: number;
	name: string;
	startDate: string;
	endDate: string;
}

export interface ExFactoryCandidate {
	id: number;
	name: string;
	startDate: string;
	endDate: string;
	isLinked: boolean;
}

// MSL - dostepnosc: kandydat listy do powiazania z kontraktem (analog ExFactoryCandidate).
export interface MslAvailabilityCandidate {
	id: number;
	name: string;
	startDate: string;
	endDate: string;
	isLinked: boolean;
}

export interface ContractDetail {
	contractId: number;
	producerId?: number | null;
	name: string;
	producerName: string;
	year: number;
	quarter: string;
	contract_type: string;
	pricingMode: "PURCHASE" | "EXFACTORY";
	ownerUserId?: number;
	ownerName?: string;
	ownerEmail?: string;
	minTotalSalesValue?: number;
	minTotalPurchaseValue?: number;
	minTotalSalesQuantity?: number;
	minTotalPurchaseQuantity?: number;
	marketingGuaranteed?: number;
	exposureRealized?: boolean | null;
	exposureDecidedAt?: string | null;
	exposureNote?: string | null;
	sumSalesPercentsOnPurchase: boolean;
	producerSettled: boolean;
	producerSettledAt?: string | null;
	producerSettledNote?: string | null;
	isActive: boolean;
	startDate?: string;
	endDate?: string;
	exfactoryLists: ExFactoryBrief[];
	pharmacies: PharmacyBrief[];
	brands: ContractBrandDetail[];
	productSurcharges: ContractProductSurchargeDetail[];
	mslItems: ContractMslItemDetail[];
	mslWymagalnoscTryb: string;
	mslProcentWymagalnosciGlobal?: number | null;
	contractTiers: SettlementTierDetail[];
	baskets: BasketDetail[];
}

export interface PharmacyBrief {
	unitcode: string;
	unitname: string;
	region?: string;
	startDate: string;
	endDate: string;
}

export interface CreateContractRequest {
	name: string;
	producerId: number;
	year: number;
	quarter: string;
	contractType?: string;
	pricingMode: "PURCHASE" | "EXFACTORY";
	ownerUserId?: number | null;
	minTotalSalesValue?: number | null;
	minTotalPurchaseValue?: number | null;
	minTotalSalesQuantity?: number | null;
	minTotalPurchaseQuantity?: number | null;
	marketingGuaranteed?: number | null;
	exposureRealized?: boolean | null;
	exposureDecidedAt?: string | null;
	exposureNote?: string | null;
	sumSalesPercentsOnPurchase?: boolean | null;
	pharmacies?: Array<{
		unitcode: string;
		startDate: string;
		endDate: string;
	}>;
	brands?: Array<{
		clientId: string;
		name: string;
		minPurchaseExfactoryValue?: number;
		purchaseValue100Exfactory?: number;
		settlementTiers?: Array<{
			thresholdPercent: number;
			settlementPercent?: number;
			settlementAmount?: number;
		}>;
	}>;
	productSurcharges?: Array<{
		bloz07: string;
		doplataSellInProcent?: number;
		doplataSellInKwota?: number;
		doplataSellOutProcent?: number;
		doplataSellOutKwota?: number;
	}>;
	mslItems?: Array<{
		bloz07: string;
		mslDostepnosc: number;
		procentWymagalnosci?: number;
	}>;
	mslWymagalnoscTryb?: string;
	mslProcentWymagalnosciGlobal?: number | null;
	contractTiers?: Array<{
		salesFrom: number;
		salesTo?: number;
		purchaseFrom: number;
		purchaseTo?: number;
		salesPercent: number;
		purchasePercent: number;
	}>;
	baskets: Array<{
		name: string;
		products: Array<{
			bloz07: string;
			brandClientId?: string;
		}>;
		settlements: Array<{
			startDate: string;
			endDate: string;
			totalSalesValue?: number;
			totalPurchaseValue?: number;
			marketingFixedAmount?: number;
			marketingFixedPercent?: number;
			marketingPurchaseAmount?: number;
			marketingSalesAmount?: number;
			marketingSalesPercent?: number;
			marketingPurchasePercent?: number;
			exhibitionFixedAmount?: number;
			exhibitionFixedPercent?: number;
			exhibitionPurchaseAmount?: number;
			exhibitionPurchasePercent?: number;
			exhibitionSalesPercent?: number;
			mslFixedAmount?: number;
			mslFixedPercent?: number;
			mslPurchaseAmount?: number;
			mslPurchasePercent?: number;
			mslSalesAmount?: number;
			mslSalesPercent?: number;
			reportingPurchaseAmount?: number;
			reportingPurchasePercent?: number;
			reportingSalesPercent?: number;
			reportingSalesAmount?: number;
			tiers: Array<{
				salesFrom: number;
				salesTo?: number;
				purchaseFrom: number;
				purchaseTo?: number;
				salesPercent: number;
				purchasePercent: number;
			}>;
		}>;
	}>;
	exfactoryPriceListIds?: number[];
	mslAvailabilityListIds?: number[];
}

export interface ExFactoryPriceListListItem {
	id: number;
	name: string;
	producerId?: number | null;
	producerName?: string;
	startDate: string;
	endDate: string;
	productCount: number;
	useForContracts: boolean;
}

export interface ExFactoryPriceItem {
	bloz07: string;
	name: string;
	price: number;
	price100: number;
}

export interface ContractChannelSummary {
	awPurchase: number;
	afPurchase: number;
	awSales: number;
	afSales: number;
}

export interface ContractBacktestRow {
	contractId: number;
	contractName: string;
	producerName: string;
	franczyzaPharmacyCount: number;
	ownPharmacyCount: number;
	salesExfactoryValue: number;
	purchaseExfactoryValue: number;
	franczyzaSalesValue: number;
	ownSalesValue: number;
	franczyzaPurchaseValue: number;
	ownPurchaseValue: number;
}

export type BacktestQuarter = "Q1" | "Q2" | "Q3" | "Q4";

export interface ExFactoryPriceListDetail {
	id: number;
	name: string;
	producerId?: number | null;
	producerName?: string;
	startDate: string;
	endDate: string;
	useForContracts: boolean;
	items: ExFactoryPriceItem[];
}

export interface CreateExFactoryPriceListRequest {
	name: string;
	producerId?: number | null;
	startDate: string;
	endDate: string;
	useForContracts?: boolean;
	items: Array<{
		bloz07: string;
		price: number;
		price100: number;
	}>;
}

// ── MSL — dostepnosc (stany w aptekach) ──────────────────────────────────────
export interface MslAvailabilityListItem {
	id: number;
	name: string;
	producerId?: number | null;
	producerName?: string | null;
	ownerUserId?: number | null;
	ownerName?: string | null;
	startDate: string;
	endDate: string;
	productCount: number;
}

export interface MslAvailabilityItem {
	bloz07: string;
	name: string;
	packCount: number;
}

export interface MslAvailabilityDetail {
	id: number;
	name: string;
	producerId?: number | null;
	producerName?: string | null;
	ownerUserId?: number | null;
	ownerName?: string | null;
	startDate: string;
	endDate: string;
	items: MslAvailabilityItem[];
}

export interface CreateMslAvailabilityRequest {
	name: string;
	producerId?: number | null;
	ownerUserId?: number | null;
	startDate: string;
	endDate: string;
	items: Array<{
		bloz07: string;
		packCount: number;
	}>;
}

export interface ExFactorySegment {
	bloz07: string;
	productName: string;
	priceListId: number;
	priceListName: string;
	effectiveFrom: string;
	effectiveTo: string;
	price: number;
}

export type {
	ProductItem,
	ProductSearchParams as ProductSearchQuery,
} from "@/features/products/types";

type ProductSearchQuery = ProductSearchParams;

export interface CategoryTreeNode {
	code: string;
	label: string;
	parentCode: string | null;
	level: number;
	sortOrder: number;
}

export interface PharmacyItem {
	id: number;
	unitcode: string;
	unitname: string;
	fullname?: string;
	/** Nazwa widoczna w raportach (nadpisanie → nazwa pełna → unitname).
	 *  Selektor pokazuje ją w drugiej linii, żeby dało się skojarzyć pozycję
	 *  listy z wierszem raportu. */
	reportName?: string;
	region?: string;
	wlasna: boolean;
	active: boolean;
	robotfetchdata: boolean;
	debugInfo?: string;
}

export interface PharmacySearchQuery {
	q?: string;
	regions?: string[];
	wlasna?: boolean;
	activeOnly?: boolean;
	limit?: number;
	/** Filtr po `farm_firm.robotfetchdata` — gdy `true`, ukrywa apteki bez
	 * synchronizacji Robot daemon (puste w MV stanu i sprzedazy). Uzywane
	 * w selektorze wyprzedazy magazynu. */
	robotfetchdata?: boolean;
	/** Filtr apteki franczyzowe (AF) — gdy `true`, zwraca tylko apteki z regionem
	 * `O-%`. Używane w Analizie korzyści RDA. */
	franczyza?: boolean;
}

export interface PharmacySearchResponse {
	items: PharmacyItem[];
	debugInfo: string;
}

export interface PharmacyDirectoryRow {
	idfirm: number;
	unitname: string;
}

export interface ContractInactivePharmacyRow {
	unitcode: string;
	unitname: string;
	region?: string;
	startDate: string;
	endDate: string;
	hasPurchases: boolean;
	hasSales: boolean;
}

export interface InactivePharmacyRow {
	unitcode: string;
	unitname: string;
	region?: string;
	manager: string;
	contractId: number;
	contractName: string;
	producerName: string;
	year: number;
	quarter: string;
	startDate: string;
	endDate: string;
	hasPurchases: boolean;
	hasSales: boolean;
}

export interface ProducerRow {
	id: number;
	idcentralfirm?: number;
	nazwa?: string;
	picName?: string;
}

export interface ContractorRow {
	id: number;
	idcentralfirm?: number;
	nazwa?: string;
	picName?: string;
	connectedFirm?: boolean;
	includetoneworder?: boolean;
	linkCount?: number;
	/** Wariant formuły ceny po rabatach (`hurt_formula_ceny`); default 'standard'. */
	formulaWariant: string;
}

/** Źródło cennika hurtowego (FTP) dla kontrahenta, per segment (baza/franczyza).
 * Hasło jest write-only: backend nigdy nie zwraca plaintextu, tylko flagę
 * `hasloUstawione`. Przy zapisie puste `haslo` zostawia istniejące hasło. */
export interface CennikZrodlo {
	segment: "baza" | "franczyza";
	typ: "ftp_xml" | "ftp_frapo" | "comarch";
	aktywne: boolean;
	host?: string;
	port?: number;
	katalog?: string;
	login?: string;
	hasloUstawione: boolean;
	uzyjFtps: boolean;
}

/** Wiersz przeglądu wszystkich skonfigurowanych źródeł cennika (super_admin).
 * Hasło jest write-only — backend zwraca tylko flagę `hasloUstawione`. */
export interface CennikZrodloOverviewRow {
	idfdost: number;
	nazwa?: string;
	segment: "baza" | "franczyza";
	typ: "ftp_xml" | "ftp_frapo" | "comarch";
	aktywne: boolean;
	host?: string;
	port?: number;
	katalog?: string;
	login?: string;
	hasloUstawione: boolean;
	uzyjFtps: boolean;
	updatedAt?: string;
	updatedBy?: string;
}

/** Raport importu źródeł cennika z pliku Excel (backend: `ImportZrodlaReport`). */
export interface ImportZrodlaReport {
	utworzone: number;
	zaktualizowane: number;
	pominieteIstniejace: number;
	segmentow: number;
	pominieteWiersze: { row: number; powod: string }[];
	nieznaneId: number[];
}

/** Hurtownia z skonfigurowanym źródłem cennika — dla dropdownu historii cen.
 * Endpoint: GET /cenniki/hurtownie (super_admin + analyst). */
export interface CennikHurtownia {
	idfdost: number;
	nazwa?: string;
	/** Czy hurt ma aktywne źródło AF. Gdy false — dla franczyzy obowiązuje cennik AW. */
	maFranczyza: boolean;
	/** Przestrzeń kodów cennika. `undefined` = typ źródła, którego jeszcze nie znamy. */
	kodTyp?: "bloz07" | "ean";
}

/** Produkt z aktualnego cennika hurtowni — wynik wyszukiwania po nazwie/BLOZ.
 * Endpoint: GET /cenniki/produkty?idfdost=&q= (super_admin + analyst). */
export interface CennikProdukt {
	bloz07: string;
	nazwa?: string;
}

/** Punkt historii ceny hurtowej per segment (baza=AW / franczyza=AF).
 * UWAGA: pola cenowe przychodzą jako STRING (rust_decimal serde-with-str) —
 * zawsze konwertuj przez Number(...) przed rysowaniem/formatowaniem. */
export interface CennikHistoriaRow {
	segment: "baza" | "franczyza";
	/** ISO datetime początku obowiązywania ceny. */
	validFrom: string;
	/** DECIMAL jako string, np. "20.88". */
	cenaNetto?: string;
}

/** Nagłówek aktualnego cennika hurtowni/segmentu (metadane pobrania).
 * Endpoint: GET /cenniki/pozycje (super_admin + analyst). */
export interface CennikNaglowek {
	/** id wpisu w `hurt_cennik` — po nim rozpoznajemy, czy pozycje pochodzą z tego pobrania. */
	id: number;
	/** ISO datetime — data pobrania cennika ( z FTP/pliku). */
	pobranoAt: string;
	/** ISO — data oferty (z pliku). */
	validFrom: string;
	liczbaPoz?: number;
	pominieteBezBloz: number;
	plikNazwa?: string;
	/** np. "ftp_xml". */
	zrodlo: string;
	status: string;
}

/** Wiersz pozycji aktualnego cennika hurtowni.
 * UWAGA: pola cenowe przychodzą jako STRING (rust_decimal) — zawsze Number(...). */
export interface CennikPozycjaRow {
	/** Kod towaru niezależny od przestrzeni: BLOZ (hurtownie apteczne) albo EAN
	 *  (arkusz FRAPO). Który to, mówi `kodTyp` — nie zgaduj po długości. */
	kod: string;
	kodTyp: "bloz07" | "ean";
	kodtowr?: number;
	nazwa?: string;
	/** DECIMAL jako string. */
	cenaNetto?: string;
	/** DECIMAL jako string. */
	vat?: string;
	validFrom: string;
}

/** Wiersz "najtańszy dostawca" — cena netto danego BLOZ u jednej hurtowni.
 * Endpoint: GET /cenniki/najtaniej?bloz07=&segment= (super_admin + analyst).
 * UWAGA: pola cenowe przychodzą jako STRING (rust_decimal) — zawsze Number(...). */
export interface NajtaniejRow {
	idfdost: number;
	nazwa?: string;
	/** DECIMAL jako string. */
	cenaNetto?: string;
	/** DECIMAL jako string. */
	vat?: string;
	segment: "baza" | "franczyza";
}

/** Wiersz rozbieżności ceny FM2 (nowy cennik) vs FM1 (ofertyitem).
 * UWAGA: pola cenowe przychodzą jako STRING (rust_decimal) — zawsze Number(...). */
export interface ZgodnoscRow {
	bloz07: string;
	nazwa?: string;
	/** DECIMAL jako string. */
	cenaFm2?: string;
	/** DECIMAL jako string. */
	cenaFm1?: string;
	status: "zgodne" | "rozjazd" | "tylko_fm2" | "tylko_fm1";
}

/** Odpowiedź walidacji zgodności FM2↔FM1: liczniki + do 500 rozbieżności. */
export interface ZgodnoscResponse {
	zgodne: number;
	rozjazd: number;
	tylkoFm2: number;
	tylkoFm1: number;
	/** Data cennika FM2 (valid_from = data wystawienia pliku), ISO. */
	cennikFm2At: string | null;
	/** Czas pobrania cennika FM2 (pobrano_at), ISO. */
	cennikFm2Pobrano: string | null;
	/** Nazwa pliku cennika FM2 (KSEDI). */
	cennikFm2Plik: string | null;
	/** Data cennika FM1 (data_imp najnowszej oferty = max(id)), ISO. */
	cennikFm1At: string | null;
	/** Nazwa pliku najnowszej oferty FM1. Równa cennikFm2Plik => ten sam cennik. */
	cennikFm1Plik: string | null;
	rozbieznosci: ZgodnoscRow[];
}

export interface TopZmianaRow {
	bloz07: string;
	nazwa: string | null;
	/** DECIMAL jako string (np. "26.89"). */
	cenaPoprzednia: string;
	cenaAktualna: string;
	deltaZl: string;
	deltaPct: string;
	dataZmiany: string;
}

/** Szczegóły błędu multi_bloz: ceny (stringi Decimal) + wybrana + krotność. */
export interface BladMultiBlozSzczegoly {
	ceny: string[];
	wybrana: string;
	krotnosc: number;
}

/** Anomalia cennika FTP wykryta przy pobieraniu. Dziś typ="multi_bloz". */
export interface BladCennikaRow {
	bloz07: string;
	nazwa: string | null;
	typ: string;
	/** Kształt zależny od typu; dla multi_bloz: BladMultiBlozSzczegoly. */
	szczegoly: BladMultiBlozSzczegoly & Record<string, unknown>;
}

/** Odpowiedź podglądu cennika: nagłówek + total (dla paginacji) + strona pozycji. */
export interface PozycjeResponse {
	/** Nagłówek cennika, Z KTÓREGO POCHODZĄ pozycje (nie „ostatnie pobranie"). */
	naglowek: CennikNaglowek | null;
	/** Ostatnie pobranie, gdy nie dało pozycji — wtedy wyświetlane ceny są starsze. */
	ostatniePobranie: CennikNaglowek | null;
	total: number;
	pozycje: CennikPozycjaRow[];
}

/** Status zlecenia ręcznego pobrania cennika (kolejka, daemon `cenniki-fetcher`).
 * `wynik` to krótkie podsumowanie, np. "zrodel=2 ok=1 bez_zmian=1 blad=0", przy
 * błędach z przyczyną per źródło ("… blad=1 — 71067/baza: FTP connect …").
 * `error` = nic się nie udało (albo "blad: ..." przy awarii całego przebiegu);
 * `done` z `blad>0` to częściowy sukces — patrz `rodzajWynikuZlecenia`.
 * Endpoint: GET /cenniki/pobierz/{jobId}. */
export interface PobierzStatus {
	status: "pending" | "running" | "done" | "error";
	wynik?: string;
	finishedAt?: string;
}

/** Nazwy plików, których fetcher szuka na FTP, per typ źródła i segment.
 *  Typ bez stałej nazwy pliku (np. `ftp_xml`, który szuka po wzorcu `Of*`)
 *  NIE ma tu klucza — brak wpisu znaczy „nie ma czego obiecać". */
export type OczekiwanePlikiZrodel = Partial<
	Record<CennikZrodlo["typ"], Record<string, string | undefined> | undefined>
>;

/** Wiersz porównania dwóch cenników. `cenaA`/`cenaB` są NULL, gdy produktu nie ma
 *  po danej stronie — front pokazuje to jako „tylko u A" / „tylko u B". */
export interface CennikPorownanieRow {
	kod: string;
	kodTyp: "bloz07" | "ean";
	nazwa?: string;
	/** DECIMAL jako string. */
	cenaA?: string;
	cenaB?: string;
	roznica?: string;
	roznicaProc?: string;
	/** Ile aptek z wybranego zakresu kupowało ten produkt w oknie. `1` oznacza
	 *  pozycję pojedynczej apteki — to one giną w uśrednieniach. */
	aptekKupujacych: number;
	ilosc?: number;
}

/** Sumy liczone na serwerze po CAŁYM przefiltrowanym zbiorze — stopka nad 200
 *  z 16 560 wierszy sumowałaby próbkę, wyglądając na sumę całości. */
export interface CennikPorownaniePodsumowanie {
	pozycji: number;
	sumaIlosc?: number;
	/** Σ(różnica × ilość) — DECIMAL jako string. Naiwna ekstrapolacja: ignoruje
	 *  rabaty, kontrakty i dostępność, a `ilosc` to zakupy produktu niezależnie
	 *  od tego, u kogo kupiony. */
	sumaWartosc?: string;
}

export interface CennikPorownanieResponse {
	pozycje: CennikPorownanieRow[];
	total: number;
	podsumowanie: CennikPorownaniePodsumowanie;
	/** Przestrzenie kodów obu źródeł. Różne (BLOZ vs EAN) = pokrycie zerowe
	 *  z definicji i trzeba to powiedzieć, a nie pokazać pustą tabelę. */
	kodTypA?: "bloz07" | "ean";
	kodTypB?: "bloz07" | "ean";
	/** Czy filtr „tylko kupowane" faktycznie zadziałał. Agregat zakupów jest
	 *  kluczowany po BLOZ, więc dla źródeł EAN-owych jest ignorowany. */
	kupowaneZastosowane: boolean;
}

export interface CennikPorownanieParams {
	aIdfdost: number;
	aSegment: string;
	bIdfdost: number;
	bSegment: string;
	scope?: string;
	tylkoKupowane?: boolean;
	miesiecy?: number;
	q?: string;
	/** JSON `[{id, value}]` z nagłówków DataTable — filtrowanie idzie na serwer. */
	columnFilters?: string;
	sortBy?: string;
	sortDir?: string;
	limit?: number;
	offset?: number;
}

export interface SetCennikZrodloRequest {
	segment: string;
	typ: string;
	aktywne: boolean;
	host?: string;
	port?: number;
	katalog?: string;
	login?: string;
	uzyjFtps: boolean;
	/** Pominięte/puste => zachowaj istniejące hasło (write-only). */
	haslo?: string;
}

export interface PurchaseDetailRow {
	bloz07: string;
	idfirm: number;
	datzk: string;
	quantity: number;
	netValue: number;
	exfactoryPrice?: number;
}

export interface ContractCompanyAnalysisRow {
	idfirm: number;
	unitname: string;
	region: string;
	datzk: string;
	bloz07: string;
	productName: string;
	iloscZakupu: number;
	iloscZakupuPrzedKorekta: number;
	exfactoryPrice?: number;
	exfactoryValue: number;
}

export interface ContractSettlementAnalysisRow {
	basketId: number;
	basketName: string;
	idfirm: number;
	unitname: string;
	region: string;
	bloz07: string;
	productName: string;
	/** Poczatek zakresu ruchu (wiersze sa zagregowane per koszyk x apteka x BLOZ). */
	firstPurchaseDate?: string;
	/** Koniec zakresu ruchu; przy pojedynczym dniu == firstPurchaseDate. */
	purchaseDate?: string;
	exfactoryPrice?: number;
	salesQuantity: number;
	salesExfactoryValue: number;
	purchaseQuantity: number;
	purchaseExfactoryValue: number;
}

export interface ContractSettlementSummaryRow {
	basketId: number;
	basketName: string;
	productCount: number;
	settlementCount: number;
	targetSalesValue: number;
	targetPurchaseValue: number;
	salesQuantity: number;
	salesExfactoryValue: number;
	purchaseQuantity: number;
	purchaseExfactoryValue: number;
	pharmacyCount: number;
	activeProductCount: number;
	missingPriceCount: number;
}

export interface ContractSettlementCompanySummaryRow {
	company: string;
	rowCount: number;
	pharmacyCount: number;
	productCount: number;
	salesQuantity: number;
	salesExfactoryValue: number;
	purchaseQuantity: number;
	purchaseExfactoryValue: number;
	missingPriceCount: number;
}

// Wykonanie kontraktow dla kierownikow (franczyza / wlasne). Odpowiedz celowo
// bez celow kontraktu i realizacji globalnej — izolacja danych miedzy sieciami.
export interface ContractExecutionRow {
	contractId: number;
	name: string;
	producerName: string;
	year: number;
	quarter: string;
	startDate: string;
	endDate: string;
	pharmacyCount: number;
	productCount: number;
	salesQuantity: number;
	salesExfactoryValue: number;
	purchaseQuantity: number;
	purchaseExfactoryValue: number;
}

export interface ContractSettlementProgressRow {
	contractId: number;
	targetSalesValue: number;
	targetPurchaseValue: number;
	realizedSalesValue: number;
	realizedPurchaseValue: number;
}

export interface ContractExecutionPharmacyRow {
	idfirm: number;
	unitname: string;
	company: string;
	manager: string;
	productCount: number;
	salesQuantity: number;
	salesExfactoryValue: number;
	purchaseQuantity: number;
	purchaseExfactoryValue: number;
}

export interface ContractMissingExfactoryPriceRow {
	basketId: number;
	basketName: string;
	idfirm: number;
	unitname: string;
	region: string;
	bloz07: string;
	productName: string;
	movementDate: string;
	salesQuantity: number;
	purchaseQuantity: number;
}

export interface ContractDailyMovementRow {
	basketId: number;
	basketName: string;
	idfirm: number;
	unitname: string;
	region: string;
	bloz07: string;
	productName: string;
	data: string;
	iloscSprzedana: number;
	wartoscBrutto: number;
	wartoscNetto: number;
	marzaWOkresie: number;
	refundacjaWOkresie: number;
	iloscZakupu: number;
	iloscZakupuPrzedKorekta: number;
	wartZakupNetto: number;
	wartZakupPoRabacieNetto: number;
	wartZakupLekospisowaNetto: number;
}

export interface ContractSettlementMonthlyRow {
	basketId: number;
	basketName: string;
	bloz07: string;
	productName: string;
	month1MinExfactoryPrice?: number;
	month1MaxExfactoryPrice?: number;
	month1SalesQuantity: number;
	month1SalesExfactoryValue: number;
	month1PurchaseQuantity: number;
	month1PurchaseExfactoryValue: number;
	month2MinExfactoryPrice?: number;
	month2MaxExfactoryPrice?: number;
	month2SalesQuantity: number;
	month2SalesExfactoryValue: number;
	month2PurchaseQuantity: number;
	month2PurchaseExfactoryValue: number;
	month3MinExfactoryPrice?: number;
	month3MaxExfactoryPrice?: number;
	month3SalesQuantity: number;
	month3SalesExfactoryValue: number;
	month3PurchaseQuantity: number;
	month3PurchaseExfactoryValue: number;
	quarterMinExfactoryPrice?: number;
	quarterMaxExfactoryPrice?: number;
	quarterSalesQuantity: number;
	quarterSalesExfactoryValue: number;
	quarterPurchaseQuantity: number;
	quarterPurchaseExfactoryValue: number;
}

// Szuflada "Porownanie z producentem" — SQL do bazy klienta (SFY).
export interface ProducerComparisonClientSqlVariant {
	key: string;
	label: string;
	sql: string | null;
	ready: boolean;
	note: string | null;
}

export interface ProducerComparisonClientSqlResponse {
	connectionName: string;
	queries: ProducerComparisonClientSqlVariant[];
}

export interface ClientConsoleResult {
	columns: string[];
	rows: (string | null)[][];
	rowCount: number;
	truncated: boolean;
	durationMs: number;
}

export const contractsApi = {
	list: () => apiFetch<ContractListItem[]>("/contracts"),
	getInactivePharmacies: (
		year: number,
		quarter: string,
		producerId?: number | null,
		ownerUserId?: number | null,
	) => {
		const params = new URLSearchParams({ year: String(year), quarter });
		if (producerId != null) params.set("producerId", String(producerId));
		if (ownerUserId != null) params.set("ownerUserId", String(ownerUserId));
		return apiFetch<InactivePharmacyRow[]>(`/contracts/inactive-pharmacies?${params}`);
	},
	// Wszystkie apteki kontraktow danego kwartalu z flagami aktywnosci (zakup/sprzedaz)
	getContractPharmaciesActivity: (year: number, quarter: string) => {
		const params = new URLSearchParams({
			year: String(year),
			quarter,
			includeActive: "true",
		});
		return apiFetch<InactivePharmacyRow[]>(`/contracts/inactive-pharmacies?${params}`);
	},
	get: (id: number) => apiFetch<ContractDetail>(`/contracts/${id}`),
	getOwnerCandidates: () => apiFetch<ContractOwnerCandidate[]>("/contracts/owner-candidates"),
	create: (data: CreateContractRequest) =>
		apiFetch<ContractDetail>("/contracts", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	update: (
		id: number,
		data: Partial<CreateContractRequest> & {
			isActive?: boolean;
			producerSettled?: boolean;
			producerSettledNote?: string | null;
		},
	) =>
		apiFetch(`/contracts/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	delete: (id: number) =>
		apiFetch(`/contracts/${id}`, {
			method: "DELETE",
		}),
	copy: (id: number, year: number, quarter: string) =>
		apiFetch<{ contractId: number }>(`/contracts/${id}/copy`, {
			method: "POST",
			body: JSON.stringify({ year, quarter }),
		}),
	listProducers: () => apiFetch<ProducerRow[]>("/contracts/producers"),

	listExFactory: () => apiFetch<ExFactoryPriceListListItem[]>("/contracts/exfactory-prices"),
	getExFactory: (id: number) =>
		apiFetch<ExFactoryPriceListDetail>(`/contracts/exfactory-prices/${id}`),
	createExFactory: (data: CreateExFactoryPriceListRequest) =>
		apiFetch<ExFactoryPriceListDetail>("/contracts/exfactory-prices", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	updateExFactory: (id: number, data: Partial<CreateExFactoryPriceListRequest>) =>
		apiFetch(`/contracts/exfactory-prices/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	deleteExFactory: (id: number, force = false) =>
		apiFetch(`/contracts/exfactory-prices/${id}${force ? "?force=true" : ""}`, {
			method: "DELETE",
		}),

	listMsl: () => apiFetch<MslAvailabilityListItem[]>("/msl-availability"),
	getMsl: (id: number) => apiFetch<MslAvailabilityDetail>(`/msl-availability/${id}`),
	createMsl: (data: CreateMslAvailabilityRequest) =>
		apiFetch<MslAvailabilityDetail>("/msl-availability", {
			method: "POST",
			body: JSON.stringify(data),
		}),
	updateMsl: (id: number, data: CreateMslAvailabilityRequest) =>
		apiFetch(`/msl-availability/${id}`, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	deleteMsl: (id: number) =>
		apiFetch(`/msl-availability/${id}`, {
			method: "DELETE",
		}),

	getExFactorySegments: (contractId: number) =>
		apiFetch<ExFactorySegment[]>(`/contracts/${contractId}/exfactory-segments`),

	getBacktest: (year: number, quarter: BacktestQuarter) =>
		apiFetch<ContractBacktestRow[]>(`/contracts/backtest?year=${year}&quarter=${quarter}`),

	getChannelSummary: (contractId: number) =>
		apiFetch<ContractChannelSummary>(`/contracts/${contractId}/channel-summary`),

	getChannelAnalysisQuarters: () =>
		apiFetch<{ year: number; quarter: BacktestQuarter }[]>("/contracts/channel-analysis/quarters"),

	getChannelAnalysis: (year: number, quarter: BacktestQuarter) =>
		apiFetch<ContractBacktestRow[]>(`/contracts/channel-analysis?year=${year}&quarter=${quarter}`),

	getExFactoryCandidates: (contractId: number) =>
		apiFetch<ExFactoryCandidate[]>(`/contracts/${contractId}/exfactory-candidates`),
	getExFactoryCandidatesByPeriod: (producerId: number, year: number, quarter: string) =>
		apiFetch<ExFactoryCandidate[]>(
			`/contracts/exfactory-candidates?producerId=${producerId}&year=${year}&quarter=${encodeURIComponent(quarter)}`,
		),
	setExFactoryPriceLists: (contractId: number, priceListIds: number[]) =>
		apiFetch<{ message: string; count: number }>(`/contracts/${contractId}/exfactory-price-lists`, {
			method: "PUT",
			body: JSON.stringify({ priceListIds }),
		}),

	getMslCandidates: (contractId: number) =>
		apiFetch<MslAvailabilityCandidate[]>(`/contracts/${contractId}/msl-availability-candidates`),
	getMslCandidatesByPeriod: (producerId: number, year: number, quarter: string) =>
		apiFetch<MslAvailabilityCandidate[]>(
			`/contracts/msl-availability-candidates?producerId=${producerId}&year=${year}&quarter=${encodeURIComponent(quarter)}`,
		),
	setMslLists: (contractId: number, listIds: number[]) =>
		apiFetch<{ message: string; count: number }>(
			`/contracts/${contractId}/msl-availability-lists`,
			{
				method: "PUT",
				body: JSON.stringify({ listIds }),
			},
		),

	searchProducts: (params: ProductSearchQuery) => {
		const searchParams = new URLSearchParams();
		if (params.q) searchParams.append("q", params.q);
		if (params.nazwa) searchParams.append("nazwa", params.nazwa);
		if (params.bloz07) searchParams.append("bloz07", params.bloz07);
		if (params.kodean) searchParams.append("kodean", params.kodean);
		if (params.contractId !== undefined) {
			const id = params.contractId.toString();
			searchParams.append("contractId", id);
			// Backward/interop fallback for handlers expecting snake_case query key.
			searchParams.append("contract_id", id);
		}
		if (params.producer) searchParams.append("producer", params.producer);
		if (params.substance) searchParams.append("substance", params.substance);
		if (params.category) searchParams.append("category", params.category.toString());
		if (params.productGroupId !== undefined) {
			searchParams.append("productGroupId", params.productGroupId.toString());
		}
		if (params.limit) searchParams.append("limit", params.limit.toString());
		if (params.offset) searchParams.append("offset", params.offset.toString());

		return apiFetch<{ items: ProductItem[]; total: number }>(
			`/products/search?${searchParams.toString()}`,
		);
	},

	searchByBlozList: (blozCodes: string[]) =>
		apiFetch<ProductItem[]>("/products/search-by-bloz-list", {
			method: "POST",
			body: JSON.stringify({ blozCodes }),
		}),

	getProducers: () => apiFetch<string[]>("/products/producers"),
	getSubstances: () => apiFetch<string[]>("/products/substances"),
	getCategoryTree: () => apiFetch<CategoryTreeNode[]>("/categories/tree"),

	searchPharmacies: (params: PharmacySearchQuery) => {
		const searchParams = new URLSearchParams();
		if (params.q) searchParams.append("q", params.q);
		if (params.regions) {
			for (const r of params.regions) {
				searchParams.append("regions[]", r);
			}
		}
		if (params.wlasna !== undefined) searchParams.append("wlasna", params.wlasna.toString());
		if (params.activeOnly !== undefined)
			searchParams.append("activeOnly", params.activeOnly.toString());
		if (params.robotfetchdata !== undefined)
			searchParams.append("robotfetchdata", params.robotfetchdata.toString());
		if (params.franczyza !== undefined)
			searchParams.append("franczyza", params.franczyza.toString());
		if (params.limit) searchParams.append("limit", params.limit.toString());

		return apiFetch<PharmacySearchResponse>(`/pharmacies/search?${searchParams.toString()}`);
	},

	getRegions: () => apiFetch<string[]>("/pharmacies/regions"),

	searchByUnitCodes: (unitCodes: string[]) =>
		apiFetch<PharmacyItem[]>("/pharmacies/search-by-unitcodes", {
			method: "POST",
			body: JSON.stringify({ unitCodes }),
		}),

	getProducersList: () => apiFetch<ProducerRow[]>("/pharmacies/producers"),

	getContractorsList: () => apiFetch<ContractorRow[]>("/pharmacies/contractors"),

	setContractorFormula: (id: number, wariant: string) =>
		apiFetch<{ ok: boolean; wariant: string }>(`/pharmacies/contractors/${id}/formula`, {
			method: "PATCH",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ wariant }),
		}),

	getContractorSources: (id: number) =>
		apiFetch<CennikZrodlo[]>(`/pharmacies/contractors/${id}/zrodla`),

	getAllContractorSources: () =>
		apiFetch<CennikZrodloOverviewRow[]>("/pharmacies/contractors/zrodla"),

	/** Jaki plik fetcher pobierze dla danego (typ źródła, segment) — patrz
	 *  `OczekiwanePlikiZrodel`. Mapowanie pochodzi z backendu, żeby front nie
	 *  trzymał własnej kopii listy nazw plików. */
	getCennikPorownanie: (p: CennikPorownanieParams) => {
		const qs = new URLSearchParams();
		for (const [k, v] of Object.entries(p)) {
			if (v !== undefined && v !== "") qs.set(k, String(v));
		}
		return apiFetch<CennikPorownanieResponse>(`/cenniki/porownanie?${qs}`);
	},

	getOczekiwanePlikiZrodel: () =>
		apiFetch<OczekiwanePlikiZrodel>("/pharmacies/contractors/zrodla/pliki"),

	setContractorSource: (id: number, body: SetCennikZrodloRequest) =>
		apiFetch<{ ok: boolean }>(`/pharmacies/contractors/${id}/zrodla`, {
			method: "PUT",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(body),
		}),

	// Import źródeł cennika z pliku Excel (.xlsx) — multipart/form-data, pole `file`.
	// apiFetch wykrywa FormData i NIE dopisuje Content-Type (browser ustawia
	// boundary sam); zachowuje Bearer token + tenant override + 401 refresh.
	importSourcesXlsx: (file: File, nadpisz: boolean) => {
		const fd = new FormData();
		fd.append("file", file);
		return apiFetch<ImportZrodlaReport>(
			`/pharmacies/contractors/zrodla/import-xlsx?nadpisz=${nadpisz}`,
			{
				method: "POST",
				body: fd,
			},
		);
	},

	getContractPurchases: (id: number) => apiFetch<PurchaseDetailRow[]>(`/contracts/${id}/purchases`),
	getContractCompanyAnalysis: (id: number) =>
		apiFetch<ContractCompanyAnalysisRow[]>(`/contracts/${id}/company-analysis`),
	getContractDailyMovements: (id: number) =>
		apiFetch<ContractDailyMovementRow[]>(`/contracts/${id}/daily-movements`),
	getContractSettlementMonthly: (id: number) =>
		apiFetch<ContractSettlementMonthlyRow[]>(`/contracts/${id}/settlement-monthly`),
	getContractSettlementSummary: (id: number) =>
		apiFetch<ContractSettlementSummaryRow[]>(`/contracts/${id}/settlement-summary`),
	getContractSettlementCompanySummary: (id: number) =>
		apiFetch<ContractSettlementCompanySummaryRow[]>(`/contracts/${id}/settlement-company-summary`),

	getContractsExecution: (scope: string, year?: number, quarter?: string) => {
		const params = new URLSearchParams({ scope });
		if (year != null) params.set("year", String(year));
		if (quarter) params.set("quarter", quarter);
		return apiFetch<ContractExecutionRow[]>(`/contracts/execution?${params.toString()}`);
	},

	getContractsSettlementProgress: () =>
		apiFetch<ContractSettlementProgressRow[]>("/contracts/settlement-progress"),

	getContractExecutionPharmacies: (id: number, scope: string) =>
		apiFetch<ContractExecutionPharmacyRow[]>(
			`/contracts/${id}/execution?scope=${encodeURIComponent(scope)}`,
		),
	getContractSettlementAnalysis: (id: number) =>
		apiFetch<ContractSettlementAnalysisRow[]>(`/contracts/${id}/settlement-analysis`),
	// Szuflada porownania: buduje 4 warianty SQL do bazy klienta (nie odpytuje jej).
	getProducerComparisonClientSql: (id: number, bloz07: string, idfirm: number) =>
		apiFetch<ProducerComparisonClientSqlResponse>(
			`/contracts/${id}/producer-comparison/client-sql?bloz07=${encodeURIComponent(bloz07)}&idfirm=${idfirm}`,
		),
	// Wykonuje (edytowalny) SELECT na bazie klienta producenta kontraktu.
	runProducerComparisonClientQuery: (id: number, sql: string) =>
		apiFetch<ClientConsoleResult>(`/contracts/${id}/producer-comparison/client-query`, {
			method: "POST",
			body: JSON.stringify({ sql }),
		}),
	getContractMissingExfactoryPrices: (id: number, basketId?: number | null) => {
		const params = new URLSearchParams();
		if (basketId != null) {
			params.set("basketId", String(basketId));
		}
		const query = params.toString();
		return apiFetch<ContractMissingExfactoryPriceRow[]>(
			`/contracts/${id}/missing-exfactory-prices${query ? `?${query}` : ""}`,
		);
	},
	getContractSettlementSurcharges: (id: number) =>
		apiFetch<ContractSurchargeSettlementRow[]>(`/contracts/${id}/settlement-surcharges`),
	getContractInactivePharmacies: (id: number) =>
		apiFetch<ContractInactivePharmacyRow[]>(`/contracts/${id}/inactive-pharmacies`),
	getPharmacyDirectory: () => apiFetch<PharmacyDirectoryRow[]>("/contracts/pharmacy-directory"),

	// ── Historia cen hurtowych (AW/AF) ─────────────────────────────────────────
	// Lista hurtowni ze skonfigurowanym źródłem cennika (super_admin + analyst).
	getCennikHurtownie: () => apiFetch<CennikHurtownia[]>("/cenniki/hurtownie"),
	// Wyszukiwarka produktów w aktualnym cenniku wybranej hurtowni (puste q => 50).
	searchCennikProdukty: (idfdost: number, q: string) =>
		apiFetch<CennikProdukt[]>(`/cenniki/produkty?idfdost=${idfdost}&q=${encodeURIComponent(q)}`),
	// Historia ceny per segment (baza/franczyza) w zakresie dat [od, do].
	getCennikHistoria: (p: { idfdost: number; bloz07: string; od: string; do: string }) =>
		apiFetch<CennikHistoriaRow[]>(
			`/cenniki/historia?idfdost=${p.idfdost}&bloz07=${encodeURIComponent(p.bloz07)}&od=${p.od}&do=${p.do}`,
		),
	// Podgląd całego aktualnego cennika hurtowni/segmentu (paginacja + wyszukiwanie server-side).
	getCennikPozycje: (p: {
		idfdost: number;
		segment: string;
		q: string;
		limit: number;
		offset: number;
		columnFilters?: string;
		sortBy?: string;
		sortDir?: string;
	}) => {
		const qs = new URLSearchParams({
			idfdost: String(p.idfdost),
			segment: p.segment,
			q: p.q,
			limit: String(p.limit),
			offset: String(p.offset),
		});
		if (p.columnFilters) qs.set("columnFilters", p.columnFilters);
		if (p.sortBy) qs.set("sortBy", p.sortBy);
		if (p.sortDir) qs.set("sortDir", p.sortDir);
		return apiFetch<PozycjeResponse>(`/cenniki/pozycje?${qs}`);
	},
	// Zlecenie ręcznego pobrania cennika (async, kolejka). idfdost pominięte => wszystkie.
	zlecPobranieCennika: (idfdost?: number) =>
		apiFetch<{ jobId: number }>("/cenniki/pobierz", {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ idfdost }),
		}),
	// Odpytanie statusu zlecenia pobrania cennika (polling co ~2.5s).
	statusPobraniaCennika: (jobId: number) => apiFetch<PobierzStatus>(`/cenniki/pobierz/${jobId}`),
	// Globalne wyszukiwanie produktu po wszystkich hurtowniach (nazwa/BLOZ).
	searchProduktGlobalny: (q: string) =>
		apiFetch<CennikProdukt[]>(`/cenniki/produkty-globalne?q=${encodeURIComponent(q)}`),
	// Najtańszy dostawca danego BLOZ w segmencie (posortowane najtaniej -> najdrożej).
	getNajtaniej: (bloz07: string, segment: "AW" | "AF") =>
		apiFetch<NajtaniejRow[]>(
			`/cenniki/najtaniej?bloz07=${encodeURIComponent(bloz07)}&segment=${segment}`,
		),
	// Walidacja zgodności ceny netto FM2 (nowy cennik FTP) vs FM1 (ofertyitem).
	getZgodnoscFm1: (idfdost: number, segment: "baza" | "franczyza") =>
		apiFetch<ZgodnoscResponse>(`/cenniki/zgodnosc-fm1?idfdost=${idfdost}&segment=${segment}`),
	// Top 100 największych zmian ceny (ostatni krok, okno 30 dni), Δ%.
	getCennikTopZmiany: (
		idfdost: number,
		segment: "baza" | "franczyza",
		kierunek: "wzrosty" | "spadki",
	) =>
		apiFetch<TopZmianaRow[]>(
			`/cenniki/top-zmiany?idfdost=${idfdost}&segment=${segment}&kierunek=${kierunek}`,
		),
	// Błędy/anomalie cennika FTP (najnowsze pobranie). Dziś tylko multi_bloz.
	getCennikBledy: (idfdost: number, segment: "baza" | "franczyza") =>
		apiFetch<BladCennikaRow[]>(`/cenniki/bledy?idfdost=${idfdost}&segment=${segment}`),
};
